/**
 * @file <bk-faq>: deep links and one-open-at-a-time for the FAQ accordion (sections/faq.liquid).
 *
 * The accordion itself is native: each question is a <details name="faq-<section>"> with its own id
 * (the block's anchor setting), so it works without JavaScript and browsers that support exclusive
 * <details> close the other answers by themselves. This element only adds what HTML cannot do yet:
 * - opens the answer a link points to (/#faq-delivery from a menu, on load, on hashchange and when
 *   the same link is clicked again), then moves focus to its question so keyboard users land there;
 * - keeps one answer open in browsers without exclusive <details>;
 * - in the theme editor, opens the question whose block is selected.
 */

class BkFaq extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    window.addEventListener('hashchange', () => this.#openFromHash(true), { signal });
    document.addEventListener('click', (event) => this.#onLinkClick(event), { signal });
    document.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event), { signal });
    if (!('name' in HTMLDetailsElement.prototype)) {
      this.addEventListener('toggle', (event) => this.#closeOthers(event), { capture: true, signal });
    }

    this.#openFromHash(false);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * @param {string} hash - location.hash or a link's hash, with its leading "#"
   * @returns {HTMLDetailsElement | null}
   */
  #itemFor(hash) {
    if (!hash || hash.length < 2) return null;
    let id = '';
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return null;
    }
    const item = this.querySelector(`details[id="${CSS.escape(id)}"]`);
    return item instanceof HTMLDetailsElement ? item : null;
  }

  /**
   * @param {HTMLDetailsElement} item
   * @param {boolean} moveFocus
   */
  #open(item, moveFocus) {
    item.open = true;
    if (!moveFocus) return;
    const summary = item.querySelector('summary');
    if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
  }

  /** @param {boolean} moveFocus */
  #openFromHash(moveFocus) {
    const item = this.#itemFor(window.location.hash);
    if (item) this.#open(item, moveFocus || document.activeElement === document.body);
  }

  /**
   * A link to the anchor that is already in the address bar fires no hashchange: open it anyway.
   * @param {MouseEvent} event
   */
  #onLinkClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
    if (!(target instanceof HTMLAnchorElement)) return;

    let url;
    try {
      url = new URL(target.href, window.location.href);
    } catch {
      return;
    }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
    const item = this.#itemFor(url.hash);
    if (item && url.hash === window.location.hash) this.#open(item, true);
  }

  /** @param {Event} event */
  #closeOthers(event) {
    const opened = event.target;
    if (!(opened instanceof HTMLDetailsElement) || !opened.open || !opened.name) return;
    for (const item of this.querySelectorAll('details')) {
      if (item !== opened && item instanceof HTMLDetailsElement && item.name === opened.name) item.open = false;
    }
  }

  /** @param {Event} event */
  #onBlockSelect(event) {
    const target = event.target;
    if (target instanceof HTMLDetailsElement && this.contains(target)) target.open = true;
  }
}

if (!customElements.get('bk-faq')) customElements.define('bk-faq', BkFaq);
